// @flow

import React, {useLayoutEffect, useRef, useState} from "react";
import Modal from "react-modal";
import type {Node} from "react";
import nestedLabelUrl from "../../assets/empco/label/garan_label_nested.svg";
import fullLabelUrl from "../../assets/empco/label/garan_label.svg";
import type {Product} from "../../models/Product";
import type {translate} from "../../i18next";
import i18n from "../../i18next";
import {formatWarrantyYears, formatWarrantyYearsText, hasWarrantyLabel} from "../../models/ManufacturerWarranty";
import {LABEL_PORTAL_URL} from "../../helper/empco/euGuarantee";
import {emitInternalEvent, toProductDetails} from "../../helper/tracker/internalEvents";
import {labelModalStyles} from "./modalStyles";
import "./empco.scss";

// Geometry of the EU GARAN templates in viewBox units. The three editable <text> placeholders were
// removed from the bundled SVGs and are drawn here at the very same positions, so the values can be
// substituted at runtime while the rest of the label stays the untouched EU artwork.
const NESTED_LABEL = {
    viewBox: "0 0 368.5 56.69",
    width: 368.5,
    height: 56.69,
    years: {x: 10.39, y: 46.65, fontSize: 41.56, maxWidth: 58, letterSpacing: "-0.02em"},
};

const FULL_LABEL = {
    viewBox: "0 0 269.29 283.46",
    width: 269.29,
    height: 283.46,
    brand: {x: 6.32, y: 74.52, fontSize: 9, maxWidth: 122},
    // right-aligned at the label's inner margin, as the "Model identifier" placeholder ends there
    model: {x: 263.65, y: 74.52, fontSize: 9, maxWidth: 122},
    years: {x: 5.07, y: 150.57, fontSize: 80, maxWidth: 112, letterSpacing: "-0.03em"},
};

type FittedTextProps = {
    x: number,
    y: number,
    fontSize: number,
    maxWidth: number,
    letterSpacing?: string,
    weight: number,
    anchor?: "start" | "end",
    children: string,
};

// SVG text that is condensed to its slot once it would overflow it (long brand names or model identifiers).
export const FittedText = ({x, y, fontSize, maxWidth, letterSpacing, weight, anchor = "start", children}: FittedTextProps) => {
    const textRef = useRef(null);
    const [fittedLength, setFittedLength] = useState(null);

    useLayoutEffect(() => {
        setFittedLength(null);
    }, [children, maxWidth]);

    useLayoutEffect(() => {
        const node = textRef.current;
        if (fittedLength !== null || !node || typeof node.getComputedTextLength !== "function") {
            return;
        }
        if (node.getComputedTextLength() > maxWidth) {
            setFittedLength(maxWidth);
        }
    }, [fittedLength, children, maxWidth]);

    return (
        <text ref={textRef}
              x={x}
              y={y}
              textAnchor={anchor}
              textLength={fittedLength || undefined}
              lengthAdjust={fittedLength ? "spacingAndGlyphs" : undefined}
              style={{
                  fontFamily: "Inter, Arial, sans-serif",
                  fontSize,
                  fontWeight: weight,
                  letterSpacing,
                  fill: "#231f20",
              }}
        >
            {children}
        </text>
    );
};

type LabelGraphicProps = {
    geometry: {viewBox: string, width: number, height: number},
    imageUrl: string,
    title: string,
    className?: string,
    children: Node,
};

const LabelGraphic = ({geometry, imageUrl, title, className, children}: LabelGraphicProps) => (
    <svg className={className} viewBox={geometry.viewBox} role="img" aria-label={title}
         xmlns="http://www.w3.org/2000/svg">
        <title>{title}</title>
        <image href={imageUrl} x="0" y="0" width={geometry.width} height={geometry.height}/>
        {children}
    </svg>
);

export type WarrantyLabelProps = {
    product: Product,
    euShop: boolean,
    t: translate,
    // "detail": product page (label + text line), "compact": cart rows
    variant?: "detail" | "compact",
};

// EU GARAN label (#56498): nested label that opens the full label on click, shown only for EU shops and
// products whose manufacturer guarantee exceeds the legal two years.
const WarrantyLabel = ({product, euShop, t, variant = "detail"}: WarrantyLabelProps) => {
    const [showFullLabel, setShowFullLabel] = useState(false);

    if (!hasWarrantyLabel(product, euShop)) {
        return null;
    }

    const {trademark, manufacturerWarranty} = product;
    const years = formatWarrantyYears(manufacturerWarranty.durationYears);            // label graphic: EU comma
    const yearsText = formatWarrantyYearsText(manufacturerWarranty.durationYears, i18n.language); // sentence: locale
    const hint = t("warranty.label.hint", {years: yearsText});
    const title = t("warranty.label.title");

    const openFullLabel = () => {
        emitInternalEvent("warranty_label_view", {product: toProductDetails(product)});
        setShowFullLabel(true);
    };
    const closeFullLabel = () => setShowFullLabel(false);

    return (
        <div className={`hpm-warrantyLabel hpm-warrantyLabel--${variant}`} data-testid="warranty-label">
            <button type="button"
                    className="hpm-warrantyLabel-nested"
                    onClick={openFullLabel}
                    title={t("warranty.label.open")}
                    aria-label={`${title}: ${hint}. ${t("warranty.label.open")}`}
            >
                <LabelGraphic geometry={NESTED_LABEL} imageUrl={nestedLabelUrl} title={`${title}: ${hint}`}>
                    <FittedText {...NESTED_LABEL.years} weight={800}>{years}</FittedText>
                </LabelGraphic>
            </button>
            {variant === "detail" &&
                <span className="hpm-warrantyLabel-text">
                    {hint}
                    {" "}
                    <button type="button" className="hpm-warrantyLabel-more" onClick={openFullLabel}>
                        {t("warranty.label.open")}
                    </button>
                </span>
            }
            <Modal isOpen={showFullLabel}
                   style={labelModalStyles}
                   shouldCloseOnOverlayClick={true}
                   onRequestClose={closeFullLabel}
                   contentLabel={title}
            >
                <button type={"button"} aria-label={t("footer.closeModal")} onClick={closeFullLabel}
                        className="hpm-modalClose"/>
                <div className="hpm-modalContent hpm-warrantyLabelModal" style={{fontFamily: "var(--font100)"}}>
                    <LabelGraphic className="hpm-warrantyLabelModal-label"
                                  geometry={FULL_LABEL}
                                  imageUrl={fullLabelUrl}
                                  title={`${title}: ${hint}`}>
                        {trademark &&
                            <FittedText {...FULL_LABEL.brand} weight={400}>{trademark}</FittedText>
                        }
                        {manufacturerWarranty.modelIdentifier &&
                            <FittedText {...FULL_LABEL.model} anchor="end" weight={400}>
                                {manufacturerWarranty.modelIdentifier}
                            </FittedText>
                        }
                        <FittedText {...FULL_LABEL.years} weight={800}>{years}</FittedText>
                    </LabelGraphic>
                    <dl className="hpm-warrantyLabelModal-values">
                        {trademark &&
                            <>
                                <dt>{t("warranty.label.brand")}</dt>
                                <dd>{trademark}</dd>
                            </>
                        }
                        {manufacturerWarranty.modelIdentifier &&
                            <>
                                <dt>{t("warranty.label.model")}</dt>
                                <dd>{manufacturerWarranty.modelIdentifier}</dd>
                            </>
                        }
                        <dt>{t("warranty.label.duration")}</dt>
                        <dd>{t("warranty.label.years", {years: yearsText})}</dd>
                    </dl>
                    <a className="hpm-warrantyLabelModal-link"
                       href={LABEL_PORTAL_URL}
                       target={"_blank"} rel={"noopener noreferrer"}>
                        {t("warranty.label.euLink")}
                    </a>
                </div>
            </Modal>
        </div>
    );
};

export default WarrantyLabel;
